/**
 * `src/platform/` — the session state machine (WS-C §2.6), now over an HttpOnly refresh cookie
 * (decisions D24 to D26; rulings R-38, R-43, R-45, R-48, R-49, R-53, R-64, R-70, R-74, R-76,
 * R-77, R-83, R-87, R-99, R-100, R-103).
 *
 * ⛔ WRITTEN FROM SCRATCH rather than adopted from the old console's `useAuth`, for two
 * measured reasons in that hook — neither of them injectable:
 *
 *   · `const REFRESH_TOKEN_KEY = 'cp_refresh_token'`. Adopting it would have put two writers
 *     on one rotation-sensitive `localStorage` slot on one origin. See `./tokens.ts` for the
 *     full argument and for why a grep gate cannot see it.
 *   · `window.location.href = '/login'` in `logout`. At the time, on
 *     admin.zion.witness.ai, that was the OLD console: this shell's Sign out button would
 *     have dropped the operator into the app we were replacing.
 *
 * Everything else is the same state machine, because every clause in the original was
 * earned.
 *
 * ## What the cookie changed here, in three places
 *
 * 1. ⛔ THE MOUNT EFFECT HYDRATES WITHOUT SEEING THE CREDENTIAL (D24, R-70). The old guard was
 *    `if (readRefreshToken() === null) settle`. A cold boot cannot see an HttpOnly cookie, so
 *    that guard now means "sign every returning operator out". The boot asks when the session
 *    marker or a legacy token says this browser holds a session, and refreshes: `{}` plus the
 *    transport header, or `{ refreshToken }` while a legacy token lives. The legacy key moves
 *    into module memory inside the refresh lock (R-99), and every refresh re-checks for session
 *    evidence there before it sends (R-100). A 401 settles signed out; a transient failure holds
 *    `isLoading` through one retry (R-38, R-53).
 * 2. ⛔ `setTokens` TAKES A UNION. A `CpCookieSessionResponse` clears the legacy key for good; a
 *    `CpTokenPairResponse` from an N-1 cp-auth-api pod is written back to it and the tab stays on
 *    the body arm (R-10, R-47).
 * 3. ⛔ LOGOUT MINTS A BEARER FIRST WHEN IT HAS NONE (D25, R-41, R-49). Script cannot delete an
 *    HttpOnly cookie, so the old "no stored token, post nothing" skip would leave a live 30-day
 *    family behind a signed-out browser and the next page load would hydrate it back.
 *
 * ## The CP second factor (kernel apps, WS-9b, `design-docs/WS-9.md` section 3.5)
 *
 * A password login can answer a wait in place of a session, and an OAuth login lands on
 * `/login/second-factor#sf=<token>`. Either way the hook keeps the second-factor token in a ref,
 * never in React state, a prop, a URL, a log or Web Storage (F-02). `secondFactor` in the state
 * holds the ceremony and the expiry alone. `completeSecondFactor` runs the WebAuthn ceremony and
 * sends the verify inside `REFRESH_LOCK_NAME`, and a session adopts there through `setTokens` (R-87).
 *
 * ## CP passkey recovery (kernel apps stage 5, WS-31b, R-150, R-152)
 *
 * An `assert` wait names the recovery proofs it takes. `proveRecovery` posts one proof with the token
 * of the wait; a passed proof answers a `recovery_enrol` wait with a new token, which replaces the old
 * one in the same ref, and the wait turns into a `register` ceremony with `recovered: true`. The
 * register then runs through `completeSecondFactor`, as for a first passkey. A verify that issued
 * ten recovery codes returns them once in its result, and a session answer then keeps the wait, so
 * the page holds the console until the operator saved the codes and called `abandonSecondFactor`.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { startAuthentication, startRegistration } from '@simplewebauthn/browser';
import {
  SECOND_FACTOR_DEVICE_NAME_MAX,
  SECOND_FACTOR_TOKEN_TTL_SECONDS,
  SITE_ROLE_SLUG_PATTERN,
  secondFactorCeremonyOf,
  secondFactorDisplayText,
  type RecoveryMethod,
  type SecondFactorCeremony,
  type SecondFactorVerifyRequest,
  type SecondFactorWait,
} from '@zion/sdk-contracts';
import {
  TransientRefreshError,
  changePassword as apiChangePassword,
  login as apiLogin,
  logout as apiLogout,
  oauthExchange as apiOauthExchange,
  passkeyLogin as apiPasskeyLogin,
  passkeyLoginOptions,
  proveBreakGlass as apiProveBreakGlass,
  proveRecoveryCode as apiProveRecoveryCode,
  proveRecoveryEmail as apiProveRecoveryEmail,
  refreshTokens as apiRefresh,
  switchActiveRole as apiSwitchActiveRole,
  sendRecoveryEmail as apiSendRecoveryEmail,
  secondFactorOptions as apiSecondFactorOptions,
  secondFactorRefusalOf,
  secondFactorVerify as apiSecondFactorVerify,
  type AuthTokens,
} from './auth.api';
import { getAccessToken, setAccessToken, setTokenRefreshFn } from '../http/client';
import { decodeToken, isTokenExpired, isTokenExpiringSoon } from './jwt';
import {
  clearLegacyRefreshToken,
  clearSessionPresent,
  getLegacyRefreshToken,
  hasSessionPresent,
  hasStoredLegacyRefreshToken,
  markSessionPresent,
  migrateLegacyRefreshToken,
  reloadLegacyRefreshToken,
  storeLegacyRefreshToken,
} from './tokens';
import { REFRESH_LOCK_NAME, runExclusive } from './refresh-lock';
import { LOGIN_URL } from '../paths';

/** How close to expiry the proactive refresh fires. */
const REFRESH_BEFORE_SECONDS = 60;
/** How often the proactive check runs. */
const REFRESH_CHECK_INTERVAL_MS = 30_000;

/**
 * How long the boot waits before its one retry after a transient refresh failure (R-30, R-53).
 *
 * Five seconds, the same first backoff step the DP client uses. One retry, not a sequence: the
 * hold below bounds the spinner, and a retry armed after the hold settles would land a token
 * behind a login form.
 */
export const BOOT_RETRY_DELAY_MS = 5_000;

/**
 * How long `isLoading` holds while that one retry runs (R-38, amending R-30).
 *
 * ⛔ FIFTEEN SECONDS, DERIVED AS 5 PLUS 10. Five is `BOOT_RETRY_DELAY_MS`, the delay before the
 * retry leaves; ten is the allowance for its round trip. A transient boot failure is no verdict
 * on the session — the cookie in the jar may be perfectly good — so the provider keeps the
 * spinner up and lets the retry answer. A spinner with no bound is the other wrong state, so the
 * hold ends here whatever that retry is doing.
 *
 * ⛔ CLEARING THE PENDING RETRY IS HALF THE GUARD, AND DISCARDING A LATE SESSION IS THE OTHER
 * HALF (R-43, R-45). `CpAdminApp` renders the login page once `isLoading` is false and
 * `isAuthenticated` is false, and that page never reads the session, so a success adopted after
 * the settle would leave an operator signed in underneath a sign-in form. `clearTimeout` cancels
 * a pending timer and cannot recall a request already in flight, so the retry carries an
 * `isCurrent` predicate and adopts nothing once this fires. A sign-in that lands during the wait
 * therefore keeps its own token.
 *
 * ⛔ A LATE REFUSAL IS NOT DISCARDED (R-83). The hold expiring means this tab stopped waiting;
 * the 401 means the session is over and cp-auth-api emptied the jar with it (R-51). So the
 * refusal still clears memory and lowers the marker, unless a mint moved `mintEpochRef` while
 * the request was out.
 */
export const BOOT_RETRY_GRACE_MS = 15_000;

/**
 * The role switch was not sent: the pre-refresh left no live access token (a transient refresh
 * failure, or a refused session). `switchActiveRole` throws it before any request.
 */
export class SwitchNotSentError extends Error {
  constructor() {
    super('The session could not be renewed, so the switch was not sent.');
    this.name = 'SwitchNotSentError';
  }
}

/**
 * What one refresh attempt decided.
 *
 * ⛔ FOUR OUTCOMES, BECAUSE THE BOOT AND THE 401 REPLAY NEED DIFFERENT AMOUNTS OF IT. The 401
 * replay in `../http/client.ts` asks one question — is there a fresh bearer — and `doRefresh`
 * answers it with `string | null`, unchanged. The boot has to tell a refusal from a failure to
 * reach the server, because one settles the session and the other holds it (R-53); and it has to
 * name the fourth case, a verdict that arrived after this tab stopped waiting for it (R-43).
 */
type RefreshVerdict =
  | { readonly kind: 'session'; readonly accessToken: string }
  | { readonly kind: 'terminal' }
  | { readonly kind: 'transient' }
  | { readonly kind: 'stale' };

/**
 * A second-factor wait, as the pages see it. The token stays in a ref of the hook.
 *
 * - `ceremony`: the WebAuthn call the wait asks for, or `null` when the wait came from a `#sf=` link
 *   and the options answer has not named it yet (`readSecondFactorCeremony`);
 * - `expiresAt`: the epoch milliseconds after which the server refuses the token;
 * - `recoveryMethods`: the recovery proofs the page offers, in `RECOVERY_METHODS` order. A password
 *   login gives the list of its `assert` wait, and `[]` when the wait carries none (a pod from
 *   before stage 5). A `#sf=` wait carries no list, because the identity provider door puts the
 *   token alone in the fragment; when its options name `authenticate`, the list is
 *   `LINK_WAIT_RECOVERY_METHODS`. Every `register` wait has `[]`;
 * - `recovered`: `true` for the `recovery_enrol` wait of a passed proof. The operator registers a
 *   new passkey to finish the sign-in.
 */
export interface SecondFactorChallenge {
  readonly ceremony: SecondFactorCeremony | null;
  readonly expiresAt: number;
  readonly recoveryMethods: readonly RecoveryMethod[];
  readonly recovered: boolean;
}

/**
 * The recovery proofs a page offers on an `authenticate` wait from a `#sf=` link (WS-31b-E1).
 *
 * The identity provider door of cp-auth-api writes `#sf=<token>` and drops the `recoveryMethods` of
 * the wait, and the options answer names the ceremony alone. So the console cannot read the list.
 * It offers the three proofs whose routes exist on every stage-5 CP: `code`, `break-glass` and
 * `admin-reset`. The server stays the judge: a proof it does not take answers 400
 * `recovery_unavailable` or `recovery_invalid`. `email` is absent, because its route lands with
 * WS-31a3 and its mail with #361.
 */
export const LINK_WAIT_RECOVERY_METHODS: readonly RecoveryMethod[] = ['code', 'break-glass', 'admin-reset'];

/**
 * One recovery proof, as the page sends it to `proveRecovery`:
 *
 * - `code`: a recovery code, normalised by `recoveryCodeKey`;
 * - `email`: the six digits of the recovery mail;
 * - `break-glass`: the 43-character token of `bin/cp-break-glass`.
 */
export type RecoveryProof =
  | { readonly method: 'code'; readonly code: string }
  | { readonly method: 'email'; readonly code: string }
  | { readonly method: 'break-glass'; readonly token: string };

/**
 * What a completed second factor gave: a session this tab adopted, or a redirect to follow.
 *
 * Either arm carries `recoveryCodes` when the verify issued ten new recovery codes (R-152 Q-5). The
 * hook holds them nowhere else; the page shows them once.
 */
export type SecondFactorResult =
  | { readonly kind: 'session'; readonly recoveryCodes?: readonly string[] }
  | { readonly kind: 'redirect'; readonly redirectUrl: string; readonly recoveryCodes?: readonly string[] };

/** The challenge of an in-band wait: a login answer or a passed recovery proof. */
function challengeOfWait(wait: SecondFactorWait): SecondFactorChallenge {
  return {
    ceremony: secondFactorCeremonyOf(wait.mode),
    expiresAt: Date.now() + wait.expiresIn * 1000,
    recoveryMethods: wait.recoveryMethods ?? [],
    recovered: wait.mode === 'recovery_enrol',
  };
}

export interface AuthState {
  readonly isAuthenticated: boolean;
  /**
   * ⛔ Starts `true` and only ever goes false ONCE, and this is the whole reason the flag
   * exists. `isAuthenticated` is `false` on the first render and flips asynchronously once
   * the refresh credential is exchanged, so any gate that branches on
   * `!isAuthenticated` before `isLoading` clears kicks every returning operator to
   * `/login` on every page load. `CpAdminApp`'s branch 1 depends on this.
   */
  readonly isLoading: boolean;
  readonly mustChangePassword: boolean;
  readonly operatorId: string | null;
  readonly email: string | null;
  /** Role slugs from the token. Drives NAV ONLY — never an authorization decision. */
  readonly siteRoles: readonly string[];
  /** Union of the permissions of every `siteRoles` member, expanded server-side at mint time. */
  readonly sitePermissions: readonly string[];
  /**
   * The site roles the person holds, from the `heldSiteRoles` claim of `ControlPlaneJwtPayload`
   * (plan 2026-10-22-cp-active-role, D1), or `null`. `null` covers three tokens: a token with no
   * such claim, the union token of a CP whose K1 gate is off (R-027), and a claim that is not a
   * list of site role slugs (`SITE_ROLE_SLUG_PATTERN`). With the claim, `siteRoles` names the one
   * active role of the session, or none. Display only: never an authorization decision.
   */
  readonly heldSiteRoles: readonly string[] | null;
  /**
   * The default role of the session, from the `restSiteRole` claim (K1, K2, R-004), or `null` when
   * the claim is absent, `null` or not a site role slug. The session returns to it at the end of an
   * elevation, and the console names it in the expiry text.
   */
  readonly restSiteRole: string | null;
  /**
   * The end of the session's elevation in epoch ms, from the `elevatedUntil` claim, or `null` when
   * the session is not elevated or the token carries no such claim.
   */
  readonly elevatedUntilMs: number | null;
  /**
   * The second-factor wait of this tab, or `null`. A session adoption and a sign-out keep it; only
   * a `session` answer of `completeSecondFactor` with no recovery codes and `abandonSecondFactor`
   * end it. A `redirect` answer, and a `session` answer that carries codes, keep it, so the page
   * stays on every path until the browser leaves or the operator goes on. `isAuthenticated` does
   * not depend on it.
   */
  readonly secondFactor: SecondFactorChallenge | null;
}

export interface UseAuthReturn extends AuthState {
  readonly login: (email: string, password: string) => Promise<void>;
  readonly loginWithPasskey: (email: string) => Promise<void>;
  readonly loginWithOAuthCode: (code: string) => Promise<void>;
  readonly changePassword: (currentPassword: string, newPassword: string) => Promise<void>;
  readonly logout: () => Promise<void>;
  /**
   * Make `role` the active site role of this session, through `POST ACTIVE_ROLE_SWITCH_PATH`
   * (`switchActiveRole` of `auth.api.ts`), and adopt the session the server answers. The post and
   * the adoption run inside `REFRESH_LOCK_NAME`, as every mint does (R-87).
   *
   * @param role a site role slug that the operator holds.
   * @param stepUpRequestId the approved `role-elevation` step-up request, or `null` on a first send.
   * @throws `ApiError` 403 with the `step_up_required` body for an elevation with no approved
   *   request (the caller runs it through `runWithStepUp`); `ApiError` 403 `role_not_held` or
   *   `step_up_invalid`; `ApiError` 400 for a body the server refuses; the raw failure of a
   *   network fault; `SessionResponseError` for a 200 body that is not a session;
   *   `SwitchNotSentError` when the pre-refresh leaves no live access token. The session and its
   *   claims stay as they were on every throw, except when the server refused the pre-refresh:
   *   then the session is signed out before `SwitchNotSentError`.
   */
  readonly switchActiveRole: (role: string, stepUpRequestId: string | null) => Promise<void>;
  /**
   * Exchange the refresh credential now and adopt the answer, so the claims follow the server
   * (an elevation that ended returns the session to its default role). It never throws.
   */
  readonly refreshSession: () => Promise<void>;
  /**
   * Start a wait from the token of a `#sf=` link. `ceremony` is `null` and the wait lasts
   * `SECOND_FACTOR_TOKEN_TTL_SECONDS` (300 s) from now. It writes no access token, legacy key or
   * session marker, and sends no request.
   */
  readonly resumeSecondFactor: (token: string) => void;
  /**
   * Post the options of the wait and set `ceremony` from the answer. A `token_invalid` or
   * `operator_inactive` refusal drops the token and rethrows; every other failure (a network fault,
   * a 429, a 5xx, a body the contract refuses) keeps the token and the wait and rethrows, so the
   * page can show the error and call this again. A wait that ended while the request was out
   * stays ended.
   *
   * @throws Error('no second factor waits') when no token waits; whatever `secondFactorOptions` throws.
   */
  readonly readSecondFactorCeremony: () => Promise<void>;
  /**
   * Run the ceremony of the wait and send the verify. The four steps:
   *
   * 1. It posts the options. The parse refuses options whose user verification is not `required`.
   * 2. `authenticate` runs `startAuthentication`. `register` runs `startRegistration` with the
   *    device name through `secondFactorDisplayText`, and throws Error('second factor needs a
   *    device name') for a `null` or blank name.
   * 3. It posts the verify inside `REFRESH_LOCK_NAME`, and a session adopts there through `setTokens`.
   * 4. A session ends the wait and returns `{ kind: 'session' }`. A redirect drops the spent
   *    token, keeps the wait with its ceremony, and returns `{ kind: 'redirect', redirectUrl }`. So
   *    the gate keeps the page on every path while the browser leaves, or while the page shows a
   *    refused redirect, until `abandonSecondFactor`.
   * 5. When the answer carries ten recovery codes, the result carries them as `recoveryCodes`. A
   *    session with codes adopts, drops the spent token and keeps the wait, so the page can show
   *    the codes before the console renders; the page calls `abandonSecondFactor` after the save.
   *
   * A `token_invalid` or `operator_inactive` refusal drops the token and rethrows; the state keeps
   * the wait, so the page can show the refusal until `abandonSecondFactor`. A `verification_failed`
   * or `no_challenge` refusal, a cancelled prompt and every other failure keep the token and rethrow.
   *
   * @throws Error('no second factor waits') when no token waits; the errors above.
   */
  readonly completeSecondFactor: (deviceName: string | null) => Promise<SecondFactorResult>;
  /** End the wait and drop its token. It sends no request. */
  readonly abandonSecondFactor: () => void;
  /**
   * Post one recovery proof with the token of the wait (WS-31b, R-150). A pass replaces the token
   * with the token of the `recovery_enrol` answer and sets the wait to `{ ceremony: 'register',
   * recoveryMethods: [], recovered: true }` with the new expiry. A wait that ended or changed while
   * the request was out stays as it is now. A `token_invalid` or `operator_inactive` refusal drops
   * the token and rethrows; `recovery_invalid`, `recovery_unavailable` and every other failure keep
   * the token and rethrow.
   *
   * @throws Error('no second factor waits') when no token waits; whatever the proof call throws.
   */
  readonly proveRecovery: (proof: RecoveryProof) => Promise<void>;
  /**
   * Ask the CP to mail a recovery code for the wait (WS-31a3). The token and the wait stay. The
   * refusals drop the token as `proveRecovery` does.
   *
   * @throws Error('no second factor waits') when no token waits; whatever `sendRecoveryEmail` throws.
   */
  readonly sendRecoveryEmail: () => Promise<void>;
}

export interface UseAuthOptions {
  /**
   * The one seam onto `window.location`, so `logout` is assertable without touching the
   * global. ⛔ A plain recording closure in tests — `(u) => seen.push(u)` — never `vi.fn()`.
   */
  readonly navigateExternal?: (url: string) => void;
}

const SIGNED_OUT: AuthState = {
  isAuthenticated: false,
  isLoading: false,
  mustChangePassword: false,
  operatorId: null,
  email: null,
  siteRoles: [],
  sitePermissions: [],
  heldSiteRoles: null,
  restSiteRole: null,
  elevatedUntilMs: null,
  secondFactor: null,
};

/**
 * A hard navigation, deliberately, not a router push: it guarantees no stale React state
 * survives a sign-out. `replace` rather than `assign` so the signed-in URL does not sit in
 * history for the back button to return to.
 */
function defaultNavigateExternal(url: string): void {
  window.location.replace(url);
}

/**
 * Read the claims a token carries, defensively.
 *
 * ⛔ Every field defaults so that a token minted before a claim existed FAILS CLOSED — an
 * empty nav, never an open one. `mustChangePassword` defaults to `false` rather than `true`
 * for the opposite and equally deliberate reason: defaulting it true would lock every
 * operator holding a pre-claim token out of the shell behind a rotation form.
 */
function stateFromAccessToken(accessToken: string): AuthState {
  const decoded = decodeToken(accessToken);
  const claims: Record<string, unknown> = decoded === null ? {} : { ...decoded };
  return {
    isAuthenticated: true,
    isLoading: false,
    mustChangePassword: claims['mustChangePassword'] === true,
    operatorId: decoded?.sub ?? null,
    email: decoded?.email ?? null,
    siteRoles: decoded?.siteRoles ?? [],
    sitePermissions: decoded?.sitePermissions ?? [],
    heldSiteRoles: heldRolesClaim(claims['heldSiteRoles']),
    restSiteRole: restRoleClaim(claims['restSiteRole']),
    elevatedUntilMs: elevatedUntilClaim(claims['elevatedUntil']),
    secondFactor: null,
  };
}

/** True for a string that holds `SITE_ROLE_SLUG_PATTERN`, the grammar of every stored role slug. */
function isSiteRoleSlug(value: unknown): value is string {
  return typeof value === 'string' && SITE_ROLE_SLUG_PATTERN.test(value);
}

/** The `heldSiteRoles` claim: a list of site role slugs, or `null` for an absent claim or any other value. */
function heldRolesClaim(value: unknown): readonly string[] | null {
  if (!Array.isArray(value) || !value.every(isSiteRoleSlug)) return null;
  return value;
}

/** The `restSiteRole` claim: a site role slug, or `null` for an absent or `null` claim and any other value. */
function restRoleClaim(value: unknown): string | null {
  return isSiteRoleSlug(value) ? value : null;
}

/** The `elevatedUntil` claim (a NumericDate, epoch seconds) in ms, or `null` for any other value. */
function elevatedUntilClaim(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) && value > 0 ? value * 1000 : null;
}

/** The default `isCurrent` for every refresh but the boot's one retry: nothing has moved on. */
function alwaysCurrent(): boolean {
  return true;
}

export function useAuth(options: UseAuthOptions = {}): UseAuthReturn {
  /**
   * ⛔ THE SEAM LIVES IN A REF, and that is a referential-stability requirement rather than a
   * style choice.
   *
   * `logout` is `useCallback`'d, so it must not depend on a value that changes identity every
   * render. A caller writing the natural thing —
   *
   *     useAuth({ navigateExternal: (url) => record(url) })
   *
   * — passes a fresh closure inside a fresh object literal on every render. Reading it directly
   * would rebuild `logout` every time, which cascades: `CpAdminApp`'s `onSignOut` (`[auth.logout]`)
   * churns, `AuthenticatedShell`'s `session` object churns, and `SectionHeader` re-renders on every
   * parent render. Every `useCallback`/`useMemo` in that chain becomes DECORATION — memoised in
   * appearance, unstable in fact. The old console records exactly this as a defect it had to fix,
   * one level up.
   *
   * The ref is updated on every render so a caller CAN legitimately swap the seam, while `logout`'s
   * identity never depends on it. Caught by the referential-stability test, which failed against the
   * direct read.
   */
  const navigateExternalRef = useRef(options.navigateExternal ?? defaultNavigateExternal);
  navigateExternalRef.current = options.navigateExternal ?? defaultNavigateExternal;

  const [state, setState] = useState<AuthState>({ ...SIGNED_OUT, isLoading: true });

  /**
   * The boot effect's pending retry timer, so a mint can cancel it (R-74).
   *
   * ⛔ A REF RATHER THAN THE EFFECT'S LOCAL, and the two readers are why. The effect arms the
   * timer and its cleanup clears it; `setTokens` runs outside the effect and has to clear the
   * same timer, because a login inside the boot's 15-second hold makes that armed retry a
   * refresh nobody wants. It would present the cookie the mint just replaced, and a refusal on
   * it clears the session the operator signed into one second earlier.
   *
   * ⛔ A REF ALSO KEEPS `setTokens` REFERENTIALLY STABLE. A ref's identity never changes, so the
   * `useCallback` below keeps its empty dependency list, `runRefresh` keeps its identity, and the
   * mount effect keeps from re-running and re-booting the session on every render.
   */
  const bootRetryRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  /**
   * How many sessions this tab has minted. `setTokens` increments it; `runRefresh` reads it
   * before its request and compares after (R-83).
   *
   * ⛔ IT DECIDES THE TERMINAL CLEAR, AND `isCurrent` CANNOT (R-83). A refusal means cp-auth-api
   * ended the session the request presented, and R-51 has the server empty the jar on the same
   * response. So the client must drop its own memory and lower the marker, or the browser holds a
   * marker with no cookie and the next boot asks — the state a sibling-origin plant is redeemed
   * in (R-70, R-78). `isCurrent` goes false the moment the 15-second hold expires, and a boot
   * retry refused at 16 s is the common case, so keying the clear on it left the marker up.
   *
   * ⛔ AND A MINT IS THE ONE THING THAT MAKES THE REFUSAL SOMEONE ELSE'S (R-45, R-74). A login
   * inside the hold replaces the cookie, so the late 401 refuses a credential this tab no longer
   * holds; the counter moves and the clear is skipped. Nothing else moves it, so the grace firing
   * alone does not.
   */
  const mintEpochRef = useRef(0);

  /**
   * Write the credential this response carries to the places that survive this tab, and raise the
   * session marker over it. The access token and the React state stay where they are.
   *
   * ⛔ IT IS A HELPER BECAUSE TWO CALLERS PERSIST AND ONLY ONE OF THEM ADOPTS (R-95). `setTokens`
   * runs it on the way to adopting the session. `runRefresh`'s stale arm runs it and stops there:
   * the response arrived after this tab gave up waiting, so the tab keeps rendering signed out
   * while the credential the server already rotated is kept for the next boot. Persisting from two
   * inline copies is how the pair arm and the cookie arm drift apart, so both go through here.
   *
   * ⛔ THE UNION IS NARROWED BY `'refreshToken' in tokens`, AND THE TWO ARMS ARE THE MIGRATION
   * (R-10, R-47). A `CpCookieSessionResponse` means cp-auth-api set the cookie and stripped the
   * field, so the legacy key goes and nothing readable is left behind (R-15). A
   * `CpTokenPairResponse` means an N-1 pod answered: the rotated token is this tab's only
   * credential, it has to survive a reload, and a second tab's re-read inside the refresh lock
   * has to see it, so it is written back.
   *
   * ⛔ EVERY CALLER RUNS THIS INSIDE THE REFRESH LOCK, AND THE WRITE-BACK IS WHY (R-87). The
   * body arm's legacy key is shared state that the lock orders, so adopting on the outside puts
   * this write outside the queue: a holder that acquires after the release rotates the key and
   * persists its successor, and this call then replaces that newer value with the token its own
   * response carried. `runRefresh` adopts inside its holder and runs its stale arm there too, and
   * R-87 put the four mints there as well, so every call site holds `REFRESH_LOCK_NAME`.
   *
   * ⛔ ON THE PAIR ARM THE MARKER FOLLOWS THE WRITE, AND ONLY THE WRITE (R-92). An N-1 pod sets
   * no cookie, so the written-back token is this browser's whole credential across a reload. The
   * marker is a shorter value under a different key, so a store can refuse the token's write and
   * take the marker's: the tab then reloaded holding a live marker and no legacy token, sent a
   * cookie-only refresh, and in a browser with no cookie of its own a sibling-origin plant was
   * the sole pair (CS5-1). So `storeLegacyRefreshToken`'s answer gates `markSessionPresent` on
   * that arm. The cookie arm marks as before: the credential the marker stands for is the cookie
   * cp-auth-api just set, and no Web Storage failure can take it away.
   *
   * @param tokens the session body cp-auth-api answered with, on either arm.
   * @returns nothing.
   * @throws nothing. Every write below is a `tokens.ts` helper that swallows a storage fault.
   *
   * Side effects: the legacy key and the module variable behind it, and `SESSION_PRESENT_KEY`.
   */
  const persistCredential = useCallback((tokens: AuthTokens) => {
    // ⛔ EVERY MINT AND EVERY SUCCESSFUL REFRESH RAISES THE MARKER (R-70). It is what the next
    // cold boot reads to decide whether to ask at all, and it carries the instant the refresh
    // cookie dies, so writing it here is also what slides that instant forward with the cookie's
    // own `Max-Age` (R-78). See `SESSION_PRESENT_KEY`.
    //
    // ⛔ ON THE PAIR ARM, ONLY WHEN THE TOKEN WAS PERSISTED (R-92). A marker with no token behind
    // it makes the next boot send a refresh a planted cookie can answer. See the docblock.
    if ('refreshToken' in tokens) {
      if (storeLegacyRefreshToken(tokens.refreshToken)) markSessionPresent();
    } else {
      clearLegacyRefreshToken();
      markSessionPresent();
    }
  }, []);

  /**
   * Adopt whichever session body came back: persist its credential, then take it as this tab's
   * session.
   *
   * ⛔ THE PERSISTENCE IS `persistCredential` AND THE REST IS HERE (R-95). A stale session verdict
   * keeps the credential and takes none of the rest — no access token, no React state, no epoch
   * move, no retry cancel — so the two halves are separate functions and the stale arm calls one
   * of them. See `persistCredential` for the two arms and for the marker rule.
   */
  const setTokens = useCallback(
    (tokens: AuthTokens) => {
      // ⛔ A MINT CANCELS THE BOOT'S PENDING RETRY (R-74). The retry was armed to answer a
      // question this session just answered, and firing it after the mint would exchange a cookie
      // the mint replaced. See `bootRetryRef`.
      clearTimeout(bootRetryRef.current);
      bootRetryRef.current = undefined;
      // ⛔ A MINT MOVES THE EPOCH (R-83), which is what tells a refresh already on the wire that
      // its refusal is about a session this tab has replaced. See `mintEpochRef`.
      mintEpochRef.current += 1;
      persistCredential(tokens);
      setAccessToken(tokens.accessToken);
      // The second-factor wait outlives an adoption: a boot refresh that lands after a `#sf=` link
      // resumed a wait must not end it (F-10).
      setState((prev) => ({ ...stateFromAccessToken(tokens.accessToken), secondFactor: prev.secondFactor }));
    },
    [persistCredential],
  );

  const clearAuth = useCallback(() => {
    setAccessToken(null);
    clearLegacyRefreshToken();
    // ⛔ AND THE END OF A SESSION LOWERS IT, on logout and on a terminal 401 alike (R-70). A
    // browser that leaves it raised keeps redeeming whatever cookie it is handed.
    clearSessionPresent();
    // A terminal refresh during a second-factor wait keeps the wait (F-10).
    setState((prev) => ({ ...SIGNED_OUT, secondFactor: prev.secondFactor }));
  }, []);

  /**
   * Exchange the refresh credential for a new access token, and say what happened.
   *
   * ⛔ THE WHOLE BODY RUNS UNDER A CROSS-TAB LOCK, AND THE LEGACY RE-READ IS INSIDE IT. The
   * server rotates, so two tabs that send the same body token concurrently produce one winner and
   * one casualty — the loser presents a token the winner already spent, gets a 401, and signs
   * itself out. `http/client.ts`'s `refreshInFlight` coalesces this within a tab and is blind
   * across tabs. On the cookie arm the browser's jar is the shared state and the lock still
   * orders the rotations; during the migration window the legacy key is the shared state and
   * `migrateLegacyRefreshToken()` is what reads it (D26, R-47).
   *
   * ⛔ THE MIGRATION IS THAT READ, SO IT IS INSIDE THE LOCK TOO (R-99). The first call in a
   * document moves the key into module memory and deletes it; later calls adopt a present key
   * and delete nothing. Run in the mount effect before the lock, the delete could land after a
   * peer's write-back and remove the only copy of the live successor, and this tab then sent the
   * spent token it read before the wait.
   *
   * ⛔ AND IT SENDS ONLY WITH SESSION EVIDENCE, WHOEVER CALLED IT (R-100, R-103). Evidence is a
   * legacy token in module memory or a live `SESSION_PRESENT_KEY`. A bearer in client memory is
   * not evidence: every mint and refresh raises the marker with the cookie's expiry, so a live
   * session always has a live marker, and a bearer without one belongs to a tab open past the
   * cookie's expiry. With no evidence, `clearAuth` runs, so that stale bearer leaves client
   * memory, the answer is `terminal`, and nothing goes on the wire. The mount effect's guard
   * covers the boot alone; `doRefresh` stays registered with the http client after sign-out, so a
   * protected request that answers 401 in a signed-out tab, or the proactive poll in a tab past
   * the marker's instant, would otherwise post `{}` with the transport header, and a
   * sibling-origin `Domain=witness.ai` cookie would be redeemed in a browser that holds no
   * session (CS5-1).
   *
   * ⛔ Serialised, NOT deduplicated. N tabs perform N rotations, sequentially, and each is
   * valid — see `refresh-lock.ts` for why sharing one exchange is refused.
   *
   * ⛔ `isCurrent` DECIDES ADOPTION AND NOTHING ELSE, AND IT DECIDES BEFORE THE ADOPTION, NOT
   * AFTER (R-43, R-45, R-83). The boot's one retry can still be in flight when the 15-second hold
   * expires, and `clearTimeout` cannot recall a request. Its caller passes a predicate that goes
   * false at the settle, and a session verdict past that point is reported as `stale` and adopted
   * by nobody. Undoing the adoption afterwards instead was the wrong shape: a sign-in that lands
   * during the wait puts its own token in client memory, and a late refresh that overwrote it and
   * then "undid itself" would sign the operator out one second after they signed in.
   *
   * ⛔ THE TERMINAL ARM IS KEYED ON THE MINT EPOCH INSTEAD (R-83). A refusal is cp-auth-api
   * ending the presented session, and R-51 empties the jar on the same response, so the marker
   * has to come down whether or not this tab is still waiting. It stays up only when a mint
   * landed since the request left, because then the refusal is about a session this tab has
   * already replaced. See `mintEpochRef`.
   *
   * ⛔ AND A STALE SESSION VERDICT STILL KEEPS ITS CREDENTIAL (R-95). cp-auth-api rotated on the
   * way to answering, so the credential this tab set out with is spent and the one in the
   * response is the only live one left. A boot retry that crossed the 15-second grace and met an
   * N-1 pod is the measured case: the migration had already deleted the legacy key, the pair
   * carries no `Set-Cookie`, and throwing the response away left the browser with nothing to send
   * on the next load while a live session sat on the server. So the stale arm runs
   * `persistCredential` and nothing else, keyed on the same mint epoch the terminal arm uses. A
   * mint since the request left means the credential belongs to a session this tab replaced, and
   * the response is discarded whole. `isCurrent` still decides adoption alone, so the tab keeps
   * rendering signed out until the next boot refreshes with what was kept.
   *
   * @param isCurrent asked once, synchronously, immediately before a session verdict is adopted.
   *   Defaults to always current, which is what every caller but the boot retry wants. It has no
   *   say over the terminal arm.
   * @returns `session` with the new access token, `terminal` when cp-auth-api refused the
   *   credential and the session is over or when this tab held no session evidence, cleared its
   *   auth state and sent nothing (R-100, R-103), `transient` when the request did not land and the
   *   session is kept (R-5, R-42), or `stale` when `isCurrent` said this tab has moved on.
   * @throws nothing. A refusal is a verdict, not an exception: every caller has to keep going.
   */
  const runRefresh = useCallback(
    async (isCurrent: () => boolean = alwaysCurrent): Promise<RefreshVerdict> => {
      return runExclusive(REFRESH_LOCK_NAME, async (): Promise<RefreshVerdict> => {
        // ⛔ Migrate, or re-read. Another tab ahead of us in the queue may have rotated the body
        // token, or converted to the cookie arm and deleted the key. Reusing a value read before
        // the wait is the exact race this lock exists to close, and the first call's delete has
        // to follow any peer's write-back for the same reason (R-99).
        migrateLegacyRefreshToken();
        // ⛔ NO SESSION EVIDENCE, NO REQUEST (R-100, R-103). Evidence is a live marker or a
        // legacy token in module memory. Checked after the migration, so a legacy token the key
        // held counts, and before the request, so a 401 replay or the proactive poll in a tab
        // without a session cannot redeem a planted cookie. A bearer in client memory is no
        // evidence: a live session raises the marker on every mint and refresh, so a bearer with
        // no live marker belongs to a tab open past the cookie's expiry. `clearAuth` takes that
        // bearer out of client memory. It takes no lock, and lowering a marker that is already
        // down is a no-op.
        if (getLegacyRefreshToken() === null && !hasSessionPresent()) {
          clearAuth();
          return { kind: 'terminal' };
        }
        // ⛔ READ BEFORE THE REQUEST, COMPARED AFTER (R-83). Inside the lock, so a mint queued
        // behind this refresh (R-74) is counted on the next attempt rather than this one.
        const epochAtSend = mintEpochRef.current;
        try {
          const tokens = await apiRefresh(getLegacyRefreshToken());
          if (!isCurrent()) {
            // ⛔ THE CREDENTIAL IS KEPT EVEN THOUGH THE SESSION IS NOT ADOPTED (R-95). See the
            // docblock: the server rotated, so discarding the response here strands a live
            // session — with the legacy key already drained by the migration and no cookie set
            // by an N-1 pod, the next boot has nothing to send. A mint since the request left
            // makes it someone else's answer, and then it is discarded whole.
            if (mintEpochRef.current === epochAtSend) persistCredential(tokens);
            return { kind: 'stale' };
          }
          setTokens(tokens);
          return { kind: 'session', accessToken: tokens.accessToken };
        } catch (cause) {
          // A transient failure keeps everything: the cookie is still in the jar and the legacy
          // token is still in module memory, so the next attempt has the same credential (R-5).
          if (cause instanceof TransientRefreshError) return { kind: 'transient' };
          // Terminal. A credential cp-auth-api rejects is not worth keeping, and leaving it means
          // every later page load retries a doomed exchange — and leaves the marker up over an
          // empty jar. ⛔ Unless a mint landed since this request left: that session is live and
          // the refusal is about the one it replaced (R-83).
          if (mintEpochRef.current === epochAtSend) clearAuth();
          return { kind: 'terminal' };
        }
      });
    },
    [setTokens, clearAuth, persistCredential],
  );

  /**
   * The refresh the 401 replay path calls: a fresh bearer, or `null`.
   *
   * ⛔ IT SWALLOWS THE DISTINCTION ON PURPOSE, so `../http/client.ts` is unchanged in behaviour.
   * That path has one question and one recovery: replay with a fresh bearer, or send the operator
   * to `LOGIN_URL`. The boot is the only caller that needs the three-way verdict.
   *
   * ⛔ IT STAYS REGISTERED AFTER SIGN-OUT, AND `runRefresh` IS WHAT KEEPS THAT SAFE (R-100,
   * R-103). The session-evidence check sits inside `runRefresh`'s critical section, not only in
   * the mount effect. A protected request still in flight at sign-out answers 401 and lands here
   * with no legacy token and no live marker. So does one from a tab open past the marker's
   * instant, whose bearer is still in client memory and counts for nothing. `runRefresh` clears
   * the auth state and answers `terminal` without a request, so this returns `null` and the
   * client sends the operator to `LOGIN_URL`.
   */
  const doRefresh = useCallback(async (): Promise<string | null> => {
    const verdict = await runRefresh();
    return verdict.kind === 'session' ? verdict.accessToken : null;
  }, [runRefresh]);

  /**
   * Put a usable bearer in client memory, or leave it empty. Two callers: `logout` and
   * `changePassword`.
   *
   * ⛔ WITHOUT THIS THE CREDENTIAL OUTLIVES THE LOGOUT (D25, R-41, R-49). cp-auth-api's `logout`
   * sits behind `CpJwtAuthGuard`, and the old guard posted only when the legacy key held a token.
   * The credential is an HttpOnly cookie now and script cannot delete it, so a skip here leaves a
   * live 30-day family behind a browser whose operator clicked Sign out, and the next page load
   * hydrates the session back.
   *
   * ⛔ IT TAKES THE REFRESH LOCK, SO EVERY CALLER MUST CALL IT BEFORE ENTERING ANY LOCK OF ITS
   * OWN (R-76). `logout` holds no lock, so there is nothing to re-enter there. `changePassword`
   * sends its mint under the lock, and calls this first, on the outside: the refresh completes
   * and releases before the mint acquires. Moving this call inside a holder of the same lock
   * would wait on that holder forever.
   *
   * ⛔ AND IT IS WHY `/v1/auth/change-password` CAN JOIN `NO_RETRY_PATHS` (R-76). The 401 the
   * replay path used to recover from is the expired-bearer case, and this handles it up front,
   * so the 401 that survives is a refusal the form should render.
   *
   * On either arm (R-49): the cookie arm sends `{}` and a current pod rotates the cookie; the
   * body arm sends its legacy token, and whatever comes back decides what the caller carries. A
   * refusal or a transient failure leaves client memory empty.
   */
  const mintFreshBearer = useCallback(async (): Promise<void> => {
    const held = getAccessToken();
    if (held !== null && !isTokenExpired(held)) return;
    await runRefresh();
  }, [runRefresh]);

  /**
   * ⛔ EVERY MINT SENDS UNDER THE REFRESH LOCK, AND THAT IS AN ORDERING GUARANTEE ABOUT THE
   * COOKIE (R-74). `runRefresh` holds the same lock, so a refresh in flight — this tab's boot
   * retry, or another tab's rotation — settles before a mint's request leaves. Its late 401, and
   * the `Set-Cookie` clear that rides on that 401, therefore land BEFORE the mint's own
   * `Set-Cookie`, and the operator keeps the session they just created.
   *
   * ⛔ THE IN-MEMORY GUARD IS NOT ENOUGH ON ITS OWN. `isCurrent` discards a late verdict inside
   * this tab and cannot undo a header on a response the server has already sent. The lock is the
   * half that acts on the wire.
   *
   * ⛔ `change-password` IS THE SHARPEST CASE. cp-auth-api revokes every session on it, so a
   * refresh already in flight from another tab comes back 401, and its clear would delete the
   * cookie this rotation just minted.
   *
   * ⛔ AND THE ADOPTION IS INSIDE THE CRITICAL SECTION, NOT AFTER IT (R-87). `setTokens` writes
   * an N-1 pod's pair token back to the shared legacy key, so it mutates the state this lock
   * orders. Called on the response after the release, it lands outside the queue: the next
   * holder — another tab — can rotate that key and persist its successor, and this tab's write
   * then replaces the newer value with the one the mint received. `runRefresh` already adopts
   * inside its holder for the same reason; every mint below now matches it.
   */
  /**
   * The second-factor token of this tab, or `null`. ⛔ A REF AND NEVER STATE (F-02): state shows in
   * the React devtools and travels as props, and the token is a credential for 300 s.
   */
  const secondFactorTokenRef = useRef<string | null>(null);

  /** The expiry of the wait, beside the token, so the ceremony read can keep it with no branch. */
  const secondFactorExpiresAtRef = useRef(0);

  /** Keep `token` and show `challenge`. It writes no access token, legacy key or marker. */
  const beginSecondFactor = useCallback((token: string, challenge: SecondFactorChallenge) => {
    secondFactorTokenRef.current = token;
    secondFactorExpiresAtRef.current = challenge.expiresAt;
    setState((prev) => ({ ...prev, secondFactor: challenge }));
  }, []);

  /** Drop the token and end the wait. */
  const endSecondFactor = useCallback(() => {
    secondFactorTokenRef.current = null;
    setState((prev) => ({ ...prev, secondFactor: null }));
  }, []);

  /** The token of the wait. @throws Error('no second factor waits') when none waits. */
  const waitingToken = useCallback((): string => {
    const token = secondFactorTokenRef.current;
    if (token === null) throw new Error('no second factor waits');
    return token;
  }, []);

  /**
   * Drop the token on a refusal that ends the wait on the server: `token_invalid` and
   * `operator_inactive`. The state keeps the wait, so the page shows the refusal until the operator
   * goes back. Every other failure keeps the token, because WS-6 keeps the record until its TTL.
   */
  const dropOnTerminalRefusal = useCallback((cause: unknown) => {
    const refusal = secondFactorRefusalOf(cause);
    if (refusal === 'token_invalid' || refusal === 'operator_inactive') secondFactorTokenRef.current = null;
  }, []);

  const login = useCallback(
    async (email: string, password: string): Promise<void> => {
      await runExclusive(REFRESH_LOCK_NAME, async () => {
        const answer = await apiLogin(email, password);
        if (answer.kind === 'session') {
          setTokens(answer.session);
          return;
        }
        beginSecondFactor(answer.pending.secondFactorToken, challengeOfWait(answer.pending));
      });
    },
    [setTokens, beginSecondFactor],
  );

  const resumeSecondFactor = useCallback(
    (token: string): void => {
      beginSecondFactor(token, {
        ceremony: null,
        expiresAt: Date.now() + SECOND_FACTOR_TOKEN_TTL_SECONDS * 1000,
        recoveryMethods: [],
        recovered: false,
      });
    },
    [beginSecondFactor],
  );

  const readSecondFactorCeremony = useCallback(async (): Promise<void> => {
    const token = waitingToken();
    try {
      const options = await apiSecondFactorOptions({ secondFactorToken: token });
      // A wait that ended or changed while the request was out stays as it is now.
      if (secondFactorTokenRef.current !== token) return;
      const challenge: SecondFactorChallenge = {
        ceremony: options.ceremony,
        expiresAt: secondFactorExpiresAtRef.current,
        recoveryMethods: options.ceremony === 'authenticate' ? LINK_WAIT_RECOVERY_METHODS : [],
        recovered: false,
      };
      setState((prev) => ({ ...prev, secondFactor: challenge }));
    } catch (cause) {
      dropOnTerminalRefusal(cause);
      throw cause;
    }
  }, [waitingToken, dropOnTerminalRefusal]);

  const completeSecondFactor = useCallback(
    async (deviceName: string | null): Promise<SecondFactorResult> => {
      const token = waitingToken();
      try {
        const options = await apiSecondFactorOptions({ secondFactorToken: token });
        let request: SecondFactorVerifyRequest;
        if (options.ceremony === 'authenticate') {
          // ⛔ `startAuthentication`, never raw `navigator.credentials.get()` (CLAUDE.md), and
          // outside the lock: the prompt waits on a person.
          const response = await startAuthentication({ optionsJSON: options.publicKeyOptions });
          request = { ceremony: 'authenticate', secondFactorToken: token, response };
        } else {
          const name =
            deviceName === null ? '' : secondFactorDisplayText(deviceName.trim(), SECOND_FACTOR_DEVICE_NAME_MAX);
          if (name === '') throw new Error('second factor needs a device name');
          const response = await startRegistration({ optionsJSON: options.publicKeyOptions });
          request = { ceremony: 'register', secondFactorToken: token, response, deviceName: name };
        }
        // ⛔ THE VERIFY AND ITS ADOPTION ARE INSIDE THE REFRESH LOCK (R-87), as for every mint.
        const answer = await runExclusive(REFRESH_LOCK_NAME, async () => {
          const verified = await apiSecondFactorVerify(request);
          if (verified.kind === 'session') setTokens(verified.session);
          return verified;
        });
        const codes = answer.recoveryCodes === undefined ? {} : { recoveryCodes: answer.recoveryCodes };
        if (answer.kind === 'session' && answer.recoveryCodes === undefined) {
          endSecondFactor();
          return { kind: 'session' };
        }
        // The server spent the token. The wait stays, so the page shows the codes or where the
        // sign-in goes.
        secondFactorTokenRef.current = null;
        if (answer.kind === 'session') return { kind: 'session', ...codes };
        return { kind: 'redirect', redirectUrl: answer.redirectUrl, ...codes };
      } catch (cause) {
        dropOnTerminalRefusal(cause);
        throw cause;
      }
    },
    [waitingToken, setTokens, endSecondFactor, dropOnTerminalRefusal],
  );

  const proveRecovery = useCallback(
    async (proof: RecoveryProof): Promise<void> => {
      const token = waitingToken();
      try {
        let wait: SecondFactorWait;
        if (proof.method === 'code') wait = await apiProveRecoveryCode({ secondFactorToken: token, code: proof.code });
        else if (proof.method === 'email') wait = await apiProveRecoveryEmail({ secondFactorToken: token, code: proof.code });
        else wait = await apiProveBreakGlass({ secondFactorToken: token, breakGlassToken: proof.token });
        // A wait that ended or changed while the request was out stays as it is now.
        if (secondFactorTokenRef.current !== token) return;
        beginSecondFactor(wait.secondFactorToken, challengeOfWait(wait));
      } catch (cause) {
        dropOnTerminalRefusal(cause);
        throw cause;
      }
    },
    [waitingToken, beginSecondFactor, dropOnTerminalRefusal],
  );

  const sendRecoveryEmail = useCallback(async (): Promise<void> => {
    const token = waitingToken();
    try {
      await apiSendRecoveryEmail({ secondFactorToken: token });
    } catch (cause) {
      dropOnTerminalRefusal(cause);
      throw cause;
    }
  }, [waitingToken, dropOnTerminalRefusal]);

  const loginWithPasskey = useCallback(
    async (email: string): Promise<void> => {
      const optionsJSON = await passkeyLoginOptions(email);
      // ⛔ `startAuthentication`, never raw `navigator.credentials.get()` (CLAUDE.md). The
      // library performs the base64url ↔ ArrayBuffer conversions the WebAuthn API requires;
      // handing it the server's JSON directly is the documented supported path.
      //
      // ⛔ AND IT RUNS OUTSIDE THE LOCK, DELIBERATELY. The authenticator waits on a human touching
      // a key, which can take a minute; holding a cross-tab lock across that would block every
      // other tab's rotation for the whole ceremony. The lock covers the mint call and its
      // adoption alone (R-74, R-87).
      const assertion = await startAuthentication({ optionsJSON });
      await runExclusive(REFRESH_LOCK_NAME, async () => {
        setTokens(await apiPasskeyLogin(assertion));
      });
    },
    [setTokens],
  );

  const loginWithOAuthCode = useCallback(
    async (code: string): Promise<void> => {
      await runExclusive(REFRESH_LOCK_NAME, async () => {
        setTokens(await apiOauthExchange(code));
      });
    },
    [setTokens],
  );

  const changePassword = useCallback(
    async (currentPassword: string, newPassword: string): Promise<void> => {
      // ⛔ THE PRE-REFRESH RUNS FIRST AND OUTSIDE THE LOCK (R-76). An absent or expired bearer is
      // exchanged here, where the lock is free. `/v1/auth/change-password` is on
      // `NO_RETRY_PATHS`, so the request below has no replay path and cannot re-enter the lock
      // it is holding. A 401 that survives both reaches the form as an `ApiError`.
      await mintFreshBearer();
      // ⚠️ A MINT ROUTE (R-50): cp-auth-api revokes every session and mints a fresh pair, so this
      // adopts a new session and converts the tab to the cookie arm exactly as a login does.
      // Adopting it is also what clears `mustChangePassword` — the old token still carries the claim.
      // ⛔ AND THE ADOPTION IS INSIDE THE HOLDER (R-87), so the write-back is ordered with every
      // other tab's rotation of the shared key.
      await runExclusive(REFRESH_LOCK_NAME, async () => {
        setTokens(await apiChangePassword(currentPassword, newPassword));
      });
    },
    [setTokens, mintFreshBearer],
  );

  /**
   * ⛔ THE REVOKE POSTS INSIDE THE REFRESH LOCK, AND IT RE-READS THE SHARED KEY THERE (R-77).
   * On the body arm the legacy key is what two tabs share. Another tab can rotate it between
   * this tab's bearer mint and this post, and posting the spent value gets a 204 that revokes
   * nothing: cp-auth-api never hears about the live successor, and this tab clears locally. So
   * the critical section is read-then-post, the same shape `runRefresh` uses.
   *
   * ⛔ `mintFreshBearer()` STAYS OUTSIDE IT. That helper takes and releases the same lock, so
   * nesting it here would wait on the holder that is waiting for it (R-76).
   *
   * ⛔ AND THE `LOGOUT_ATTEMPTS` REPEATS STAY INSIDE THE ONE SECTION (R-48). `apiLogout` owns
   * the retry loop, so wrapping the call wraps every attempt. A peer that rotated between two
   * attempts would otherwise put the same stale value on the wire again.
   *
   * ⛔ AND `clearAuth()` IS INSIDE THE SECTION TOO, SO A LOGIN QUEUED BEHIND IT SURVIVES (R-93).
   * The clear removes the shared legacy key and the session marker, which is the same shared
   * state this lock orders. Run after the release, it lands outside the queue: a login in
   * another tab acquires the lock, sets its cookie and raises the marker, and this tab's
   * continuation then removes both. The cookie stays live, the next reload finds no marker and
   * sends nothing, and the operator is signed out of a session they just created. It sits in a
   * `finally` so a failed revoke still clears, which is what the outer `catch` used to promise.
   * The no-bearer path takes the lock for the clear alone, for the same ordering.
   */
  const logout = useCallback(async (): Promise<void> => {
    await mintFreshBearer();
    if (getAccessToken() !== null) {
      try {
        await runExclusive(REFRESH_LOCK_NAME, async () => {
          reloadLegacyRefreshToken();
          try {
            await apiLogout(getLegacyRefreshToken());
          } finally {
            clearAuth();
          }
        });
      } catch {
        // Best-effort: the server-side session may already be gone, and a failed revoke
        // must not trap the operator in a signed-in shell they asked to leave. The clear above
        // already ran in the `finally`, so the tab is signed out either way (R-93).
      }
    } else {
      // No bearer to revoke with, so there is nothing to post — but the clear still writes the
      // shared key and the marker, so it waits its turn in the same queue (R-93).
      await runExclusive(REFRESH_LOCK_NAME, async () => {
        clearAuth();
      });
    }
    // ⛔ `LOGIN_URL`, never a literal — one definition in platform/paths.ts. See the header.
    // Read through the ref so this callback's identity does not depend on the seam.
    navigateExternalRef.current(LOGIN_URL);
  }, [clearAuth, mintFreshBearer]);

  const switchActiveRole = useCallback(
    async (role: string, stepUpRequestId: string | null): Promise<void> => {
      // The bearer must be live: the route sits behind `CpJwtAuthGuard`. Outside the lock (R-76).
      await mintFreshBearer();
      // A transient or refused pre-refresh leaves no live bearer, so the switch is not sent.
      const bearer = getAccessToken();
      if (bearer === null || isTokenExpired(bearer)) throw new SwitchNotSentError();
      await runExclusive(REFRESH_LOCK_NAME, async () => {
        setTokens(await apiSwitchActiveRole(role, stepUpRequestId));
      });
    },
    [setTokens, mintFreshBearer],
  );

  const refreshSession = useCallback(async (): Promise<void> => {
    await runRefresh();
  }, [runRefresh]);

  // Register the refresh function the http client's 401 path calls. In an effect rather
  // than at module scope because it closes over `setTokens`/`clearAuth`.
  useEffect(() => {
    setTokenRefreshFn(doRefresh);
  }, [doRefresh]);

  /*
   * On mount: look for session evidence, then hydrate (D24, R-70, R-99).
   *
   * ⛔ IT ASKS WHEN THIS BROWSER HAS SOMETHING TO ASK ABOUT, AND NOT OTHERWISE (D24 as amended
   * by R-70). The credential is an HttpOnly cookie that script cannot see, so the old
   * `readRefreshToken() !== null` guard is gone; a returning operator's browser is indicated by
   * `SESSION_PRESENT_KEY` instead, or by a legacy token in module memory or in the key. A browser
   * with none sends no request, which is what keeps a planted cookie unredeemable (CS5-1).
   *
   * ⛔ THE EFFECT ONLY LOOKS AT THE LEGACY KEY; THE MIGRATION IS INSIDE THE LOCK (R-99).
   * `hasStoredLegacyRefreshToken` reads the key and deletes nothing. `runRefresh` moves it into
   * module memory and deletes it after the lock is held, so the value this effect saw before the
   * wait is never the one sent, and the delete cannot remove a peer's newer write-back.
   *
   * ⛔ THIS GUARD IS THE EARLY RETURN FOR THE SIGNED-OUT BOOT, NOT THE WHOLE DEFENCE (R-100).
   * `runRefresh` checks for session evidence again inside its critical section, for every caller.
   * The early return here still matters: it settles `isLoading` without a lock round trip.
   *
   * ⛔ A TRANSIENT FAILURE HOLDS, A REFUSAL SETTLES (R-38, R-53). One retry at
   * `BOOT_RETRY_DELAY_MS`; the hold ends at that retry's verdict or `BOOT_RETRY_GRACE_MS`,
   * whichever comes first. When the grace fires it clears the pending retry, and a session
   * verdict that still arrives is discarded rather than adopted (R-43) — but only the token that
   * refresh itself stored is cleared, so a sign-in that landed during the wait survives (R-45).
   *
   * ⛔ A LATE REFUSAL IS THE EXCEPTION, AND IT STILL CLEARS (R-83). The hold expiring is this tab
   * giving up on an answer; a 401 is the answer, and R-51 has cp-auth-api empty the jar on it. So
   * the mint epoch, not the hold, decides the clear: with no mint since the request left, memory
   * goes and the marker comes down, and the next boot sends nothing. The grace firing on its own
   * is no verdict, so `settleSignedOut` leaves the marker and the live cookie alone.
   */
  useEffect(() => {
    /*
     * ⛔ THE BOOT'S GUARD READS A FLAG RATHER THAN A CREDENTIAL (R-70, CS5-1), AND `runRefresh`
     * REPEATS IT FOR EVERY LATER REFRESH (R-100). D24 removed the old
     * `readRefreshToken() !== null` guard because the credential became invisible to script.
     * Removing every guard made the console redeem any
     * `cp_refresh_token` the browser happened to hold, and a script on any `*.witness.ai` origin
     * can plant one with `Domain=witness.ai`. In a browser with no session of its own that
     * planted pair is the only pair, so R-11's duplicate refusal never fires and the operator is
     * signed in as the attacker.
     *
     * `hasSessionPresent()` is not the credential and cannot stand in for one: it says this
     * browser signed in here, has not signed out, and holds a marker whose instant is still in
     * the future (R-78). `localStorage` is origin-scoped, so the sibling origin that can plant
     * the cookie cannot raise this. With neither a live marker nor a legacy token, in module
     * memory or in the key, the boot settles signed out and sends nothing. The key is only read
     * here; `runRefresh` migrates it inside the lock (R-99).
     */
    if (!hasSessionPresent() && getLegacyRefreshToken() === null && !hasStoredLegacyRefreshToken()) {
      setState((prev) => ({ ...prev, isLoading: false }));
      return undefined;
    }

    let settled = false;
    let graceTimer: ReturnType<typeof setTimeout> | undefined;

    const settleSignedOut = (): void => {
      settled = true;
      setState((prev) => ({ ...prev, isLoading: false }));
    };

    const onVerdict = (verdict: RefreshVerdict, isRetry: boolean): void => {
      // The grace already fired and this tab renders signed out, so there is no state left to
      // move. `runRefresh` saw the same thing through `isCurrent` and adopted nothing (R-43,
      // R-45), though a session verdict still persisted its credential for the next boot (R-95);
      // a terminal verdict has already lowered the marker on its own account, keyed on the mint
      // epoch rather than on this flag (R-83).
      if (settled) return;
      if (verdict.kind === 'session') {
        // `setTokens` has already set `isLoading: false` and the claims.
        settled = true;
        return;
      }
      if (verdict.kind === 'transient' && !isRetry) {
        bootRetryRef.current = setTimeout(() => {
          void runRefresh(() => !settled).then((v) => {
            onVerdict(v, true);
          });
        }, BOOT_RETRY_DELAY_MS);
        graceTimer = setTimeout(() => {
          clearTimeout(bootRetryRef.current);
          settleSignedOut();
        }, BOOT_RETRY_GRACE_MS);
        return;
      }
      settleSignedOut();
    };

    void runRefresh().then((verdict) => {
      onVerdict(verdict, false);
    });

    return () => {
      clearTimeout(bootRetryRef.current);
      clearTimeout(graceTimer);
    };
  }, [runRefresh]);

  // Proactive refresh, so a long-idle tab does not discover its expiry via a failed
  // request. Gated on `isAuthenticated`, so it never runs before the boot hold settles, and
  // cleared on unmount.
  useEffect(() => {
    if (!state.isAuthenticated) return undefined;
    const interval = setInterval(() => {
      const token = getAccessToken();
      // ⚠️ Checks the ACCESS token, not the refresh credential: the cookie is invisible to script
      // and the legacy token is an opaque hex string with no decodable expiry.
      if (token !== null && isTokenExpiringSoon(token, REFRESH_BEFORE_SECONDS)) {
        void doRefresh();
      }
    }, REFRESH_CHECK_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [state.isAuthenticated, doRefresh]);

  // ⚠️ A fresh object literal every render, deliberately matching the shape consumers
  // already expect. Consumers MUST depend on the MEMBER (`auth.logout`), never on `auth` —
  // every member is `useCallback`'d and stable, but this wrapper is not, so a `[auth]`
  // dependency makes any downstream `useMemo`/`useCallback` decoration. The old console
  // records this as a real defect it had to fix.
  return {
    ...state,
    login,
    loginWithPasskey,
    loginWithOAuthCode,
    changePassword,
    logout,
    switchActiveRole,
    refreshSession,
    resumeSecondFactor,
    readSecondFactorCeremony,
    completeSecondFactor,
    abandonSecondFactor: endSecondFactor,
    proveRecovery,
    sendRecoveryEmail,
  };
}
